import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Pencil, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { LoadingState, ErrorState, EmptyState } from '@/components/ui/loading-state'
import { listEntityColRules, deleteEntityColRule } from '../api/derivationConfig'
import { EntityColRuleModal } from './EntityColRuleModal'
import type { EntityColRule } from '../types'

interface EntityColRulesTabProps {
  accId?: number
}

function ruleKey(r: EntityColRule) {
  return `${r.accId ?? 'global'}-${r.name}`
}

export function EntityColRulesTab({ accId }: EntityColRulesTabProps) {
  const queryClient = useQueryClient()
  const [showModal, setShowModal] = useState(false)
  const [editTarget, setEditTarget] = useState<EntityColRule | undefined>()
  const [deleteConfirm, setDeleteConfirm] = useState<EntityColRule | null>(null)
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null)

  const { data: rules = [], isLoading, isError, error, refetch } = useQuery({
    queryKey: ['entity-col-rules', accId],
    queryFn: () => listEntityColRules(accId),
  })

  const deleteMutation = useMutation({
    mutationFn: (r: EntityColRule) => deleteEntityColRule(r.accId, r.name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['entity-col-rules'] })
      setDeleteConfirm(null)
      showFeedback('success', 'Template deleted.')
    },
    onError: (err: Error) => {
      showFeedback('error', err.message)
      setDeleteConfirm(null)
    },
  })

  function showFeedback(type: 'success' | 'error', message: string) {
    setFeedback({ type, message })
    setTimeout(() => setFeedback(null), 3000)
  }

  function handleEdit(r: EntityColRule) {
    setEditTarget(r)
    setShowModal(true)
  }

  function handleCloseModal() {
    setShowModal(false)
    setEditTarget(undefined)
  }

  return (
    <div>
      {/* Toolbar */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-base font-semibold text-gray-900">Entity Column Templates</h2>
          <p className="text-sm text-gray-500">
            Reusable templates that define where and how a value is stored in an entity collection.
          </p>
        </div>
        <Button size="sm" onClick={() => { setEditTarget(undefined); setShowModal(true) }}>
          <Plus className="h-4 w-4 mr-1.5" />
          New Template
        </Button>
      </div>

      {feedback && (
        <div className={`mb-4 text-sm px-4 py-2 rounded-lg ${
          feedback.type === 'success'
            ? 'bg-green-50 text-green-700 border border-green-200'
            : 'bg-red-50 text-red-700 border border-red-200'
        }`}>
          {feedback.message}
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-200">
        {isLoading ? (
          <LoadingState message="Loading templates…" />
        ) : isError ? (
          <ErrorState error={error as Error} title="Failed to load templates" onRetry={() => refetch()} />
        ) : rules.length === 0 ? (
          <EmptyState
            title="No entity column templates"
            description="Templates define collection, column, key type and transforms for a logical name."
            action={{ label: 'New Template', onClick: () => setShowModal(true) }}
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-gray-50">
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Acc ID</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Name (ref key)</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Collection</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Column</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Key Type</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Transforms</th>
                  <th className="text-right px-4 py-3 font-medium text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rules.map((r, idx) => (
                  <tr
                    key={ruleKey(r)}
                    className={`border-b last:border-0 hover:bg-gray-50 transition-colors ${
                      idx % 2 !== 0 ? 'bg-gray-50/40' : ''
                    }`}
                  >
                    <td className="px-4 py-3 font-mono text-gray-700">
                      {r.accId ?? <span className="text-gray-400 italic text-xs">global</span>}
                    </td>
                    <td className="px-4 py-3">
                      <code className="text-xs bg-indigo-50 text-indigo-700 px-1.5 py-0.5 rounded font-medium">
                        {r.name}
                      </code>
                    </td>
                    <td className="px-4 py-3 text-gray-700">{r.coll}</td>
                    <td className="px-4 py-3 font-mono text-gray-700 text-xs">{r.col}</td>
                    <td className="px-4 py-3">
                      <Badge variant={r.keyType === 'fhid' ? 'warning' : 'info'}>
                        {r.keyType}
                      </Badge>
                    </td>
                    <td className="px-4 py-3">
                      {r.xform && r.xform.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {r.xform.map((x, i) => (
                            <span key={i} className="text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">
                              {x.op}{x.args && x.args.length > 0 ? `(${x.args.join(', ')})` : ''}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-gray-300 text-xs">none</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {deleteConfirm === r ? (
                        <span className="inline-flex items-center gap-2">
                          <span className="text-xs text-gray-500">Confirm delete?</span>
                          <Button size="sm" variant="destructive"
                            onClick={() => deleteMutation.mutate(r)}
                            disabled={deleteMutation.isPending}>
                            Yes
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => setDeleteConfirm(null)}>
                            No
                          </Button>
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1">
                          <Button size="sm" variant="ghost" onClick={() => handleEdit(r)}>
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button size="sm" variant="ghost"
                            onClick={() => setDeleteConfirm(r)}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50">
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && (
        <EntityColRuleModal existing={editTarget} accId={accId} onClose={handleCloseModal} />
      )}
    </div>
  )
}
